import { useNavigate } from "react-router-dom";
import {
    OrderType,
    PaymentForm,
    ProductOrderItem,
    TaskSaleAdditional,
    TaskSaleAdditionalPayload,
} from "tm-shared/types";
import { isPointDistribution } from "tm-shared/utils";

import { ROUTES } from "@/constants";
import { makeCommand, useAsyncAction, useMakeCommand } from "@/hooks";
import {
    useAppDispatch,
    usePointPriceListSelector,
    usePointSelector,
} from "@/hooks/redux";
import { buildOfflineCheckoutOrder } from "@/pages/WorkPlan/Tasks/utils";
import {
    deductInventory,
    enqueueRequestQueueItem,
} from "@/store/slices/offline";
import { clearTaskPayload } from "@/store/slices/progress";
import { addItemToCheckout, setTaskCompleted } from "@/store/slices/work";

const buildInventoryDelta = (
    products: { barcode: string; count: number }[],
): Record<string, number> =>
    products.reduce(
        (acc, { barcode, count }) => {
            acc[barcode] = (acc[barcode] ?? 0) + count;
            return acc;
        },
        {} as Record<string, number>,
    );

interface CreateCheckoutParams {
    task: TaskSaleAdditional;
    pointId: string;
    products: Record<string, ProductOrderItem>;
    shouldIgnoreRemainings: boolean;
}

export const useCreateCheckout = ({
    task,
    pointId,
    products,
    shouldIgnoreRemainings,
}: CreateCheckoutParams) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { makeTaskCompleteCommands } = useMakeCommand();

    const currentPoint = usePointSelector(pointId);
    const currentTask = task;

    const orderId = currentTask.context?.orderId;
    const taskId = task.id;

    const currentPriceList = usePointPriceListSelector(pointId);

    const prepareOfflineCheckout = (
        productsArr: ProductOrderItem[],
        paymentForm: PaymentForm,
    ) => {
        if (!currentPoint) {
            console.error("No required params point");
            return;
        }

        const checkout = buildOfflineCheckoutOrder({
            productsArr,
            point: currentPoint,
            paymentForm,
            orderId,
            currentPriceList: currentPriceList ?? {},
            orderType: OrderType.H,
        });

        dispatch(
            addItemToCheckout({
                checkoutList: {
                    [orderId]: checkout,
                },
            }),
        );
    };

    const [handleSubmit, isLoading] = useAsyncAction(
        async (paymentForm: PaymentForm) => {
            const taskValues = products ?? {};

            const orderPayload: TaskSaleAdditionalPayload = {
                marketProducts: { [orderId]: Object.values(taskValues) },
                paymentForms: { [orderId]: paymentForm },
                ...(shouldIgnoreRemainings
                    ? {
                          ignoreRemainingsOrders: {
                              [orderId]: { ignoreRemainings: true },
                          },
                      }
                    : {}),
            };

            const productsArr = Object.values(taskValues);
            prepareOfflineCheckout(productsArr, paymentForm);

            const storageId =
                currentPoint && isPointDistribution(currentPoint)
                    ? currentPoint.typeContext.storage.id
                    : null;

            if (storageId) {
                const inventoryDelta = buildInventoryDelta(productsArr);

                dispatch(
                    deductInventory({
                        storageId,
                        newInventory: inventoryDelta,
                    }),
                );
            }

            const taskCompleteCommands = await makeTaskCompleteCommands({
                params: { taskId, hasPhoto: false, payload: orderPayload },
            });

            dispatch(
                enqueueRequestQueueItem({
                    id: taskId,
                    metadata: {
                        pointId,
                        taskId,
                        orderId,
                    },
                    options: {
                        todayOnly: true,
                    },
                    commands: [
                        makeCommand({
                            type: "postOrderSave",
                            title: "Збереження заявки",
                            params: orderPayload,
                        }),

                        ...taskCompleteCommands,

                        makeCommand({
                            type: "getPointInventoryGetThunk",
                            title: "Оновлення залишків",
                            params: { pointId },
                        }),
                    ],
                }),
            );

            dispatch(setTaskCompleted({ targetId: pointId, taskId }));
            dispatch(clearTaskPayload({ taskId }));

            navigate(ROUTES.WorkPlan, { replace: true });
        },
    );

    return { handle: handleSubmit, isLoading };
};
